import { defineMessages } from "@formatjs/intl";
import React from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "src/common/components/Button";
import {
	ColumnProps,
	RowCompareFn,
	RowData,
	SortDirection,
	useFilter,
	useSort
} from "src/common/components/DataTable";
import { MobileColumnProps } from "src/common/components/MobileTable/types";
import { useFlatColumns } from "src/common/components/MobileTable/useFlatColumns";

import "./MobileTable.css";

const messages = defineMessages({
	more: {
		id: "mobile_table.more",
		defaultMessage: "Több"
	},
	less: {
		id: "mobile_table.less",
		defaultMessage: "Kevesebb"
	},
	open: {
		id: "mobile_table.open",
		defaultMessage: "Megnyitás"
	}
});

interface MobileTableProps {
	columns: MobileColumnProps;
	data: RowData[];
	query?: string;
	sortKey: string;
	sortDirection: SortDirection;
	noDataMessage?: React.ReactElement;
	onRowClick?(data: RowData): unknown;
	preCompareFn?: RowCompareFn;
}

export const MobileTable: React.FunctionComponent<MobileTableProps> = ({
	columns,
	data: tableData,
	query = "",
	sortKey,
	sortDirection,
	noDataMessage = null,
	onRowClick,
	preCompareFn
}) => {
	const flatColumns = useFlatColumns(columns);

	const filteredTableData = useFilter(flatColumns, tableData, query);
	const sortedTableData = useSort(
		flatColumns,
		filteredTableData,
		sortKey,
		sortDirection,
		preCompareFn
	);

	if (sortedTableData.length === 0) {
		return noDataMessage;
	}

	return (
		<div className="mobile-table">
			<div className="mobile-table__content">
				{sortedTableData.map((rowData, i) => (
					<MobileTableRow
						key={i}
						columns={columns}
						data={rowData}
						onClick={onRowClick}
					/>
				))}
			</div>
		</div>
	);
};

const MobileTableRow: React.FunctionComponent<{
	columns: MobileColumnProps;
	data: RowData;
	onClick?(data: RowData): unknown;
}> = ({ columns, data, onClick }) => {
	const [isOpen, setOpen] = React.useState(false);
	const onToggle = React.useCallback(() => {
		setOpen(isOpen => !isOpen);
	}, []);
	return (
		<div className="mobile-table__row">
			<div className="mobile-table__header">
				{columns.icon && (
					<div className="mobile-table__header__icon">
						{renderValue(data, columns.icon)}
					</div>
				)}
				<div className="mobile-table__header__text">
					<div className="mobile-table__header__title">
						{renderValue(data, columns.title)}
					</div>
					{columns.subtitle && (
						<div className="mobile-table__header__subtitle">
							{renderValue(data, columns.subtitle)}
						</div>
					)}
				</div>
			</div>
			{columns.fix.length !== 0 && (
				<div className="mobile-table__body mobile-table__body--fix">
					{columns.fix.map(columnProps => (
						<div
							className="mobile-table__column"
							key={columnProps.key}
						>
							<div className="mobile-table__value">
								<div>{renderValue(data, columnProps)}</div>
							</div>
						</div>
					))}
				</div>
			)}
			{columns.optional.length !== 0 && isOpen && (
				<div className="mobile-table__body mobile-table__body--optional">
					{columns.optional.map(columnProps => (
						<div
							className="mobile-table__column"
							key={columnProps.key}
						>
							<div className="mobile-table__label">
								<div>{columnProps.label}:</div>
							</div>
							<div className="mobile-table__value">
								<div>{renderValue(data, columnProps)}</div>
							</div>
						</div>
					))}
				</div>
			)}
			{(columns.optional.length !== 0 || onClick !== undefined) && (
				<div className="mobile-table__footer">
					<div className="mobile-table__footer__left">
						{columns.optional.length !== 0 && (
							<Button
								type="button"
								variant="secondary"
								onClick={onToggle}
							>
								{isOpen ? (
									<FormattedMessage {...messages.less} />
								) : (
									<FormattedMessage {...messages.more} />
								)}
							</Button>
						)}
					</div>
					<div className="mobile-table__footer__right">
						{onClick !== undefined && (
							<Button
								type="button"
								variant="primary"
								onClick={() => {
									onClick(data);
								}}
							>
								<FormattedMessage {...messages.open} />
							</Button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

function renderValue(
	data: RowData,
	{ key, render }: ColumnProps
): React.ReactNode {
	return (
		<>
			{render === undefined
				? (data[key] as React.ReactNode) || "-"
				: render(data[key], data)}
		</>
	);
}
